import type { ComponentType, SVGProps } from 'react';
import * as flags from 'country-flag-icons/react/3x2';

/**
 * Countries we accept at sign-up.
 *
 * MVP scope (apps/web/CLAUDE.md §13): UK, USA, and the EU 27. Order:
 * UK first (our home market and design-partner geography), USA second,
 * then the EU alphabetically.
 *
 * `code` is ISO 3166-1 alpha-2. We store the alpha-2 code rather than
 * the country name so the user's display language and our storage layer
 * don't drift apart.
 *
 * `Flag` is the SVG React component from `country-flag-icons` for the
 * corresponding code. 3x2 aspect ratio; renders consistently across
 * operating systems (Unicode flag emojis would render as letter pairs
 * on Windows).
 */

type FlagComponent = ComponentType<SVGProps<SVGSVGElement> & { title?: string }>;

export interface Country {
  readonly code: string;
  readonly name: string;
  readonly Flag: FlagComponent;
}

// `country-flag-icons` exports a named React component per ISO alpha-2 code.
// We index by string and assert the component shape — TypeScript can't
// statically narrow `string -> known key` from this lookup.
function flagFor(code: string): FlagComponent {
  const Component = (flags as Record<string, FlagComponent | undefined>)[code];
  if (!Component) {
    throw new Error(`No flag component for country code: ${code}`);
  }
  return Component;
}

const RAW_COUNTRIES: readonly { readonly code: string; readonly name: string }[] = [
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  // EU 27, alphabetical by English name
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'GR', name: 'Greece' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MT', name: 'Malta' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },
];

export const COUNTRIES: readonly Country[] = RAW_COUNTRIES.map((c) => ({
  ...c,
  Flag: flagFor(c.code),
}));

export const DEFAULT_COUNTRY_CODE = 'GB';

const VALID_CODES = new Set(COUNTRIES.map((c) => c.code));

export function isValidCountryCode(code: string): boolean {
  return VALID_CODES.has(code);
}
